import { GroundTransportDateTime, GroundTransportDateTimeState } from "components/AppointmentDatePicker/GroundTransportDateTime";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import GroundTransportTypeDropdown from "components/dropdowns/reactselect/GroundTransportTypeDropdown";
import { GoogleMapRoute } from "components/GoogleMap/GoogleMapRoute";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { GooglePlaceInputValue } from "components/ValidatedFields/GooglePlaceInput";
import { GooglePlaceInputWithValue } from "components/ValidatedFields/GoolgePlaceInputWithValue";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import EmailInput from "components/ValidatedFields/WithErrorLabel/EmailInput";
import { GroundTransportDurationInput } from "groundTransport/GroundTransportDurationInput";
import { PickupLocationInput } from "groundTransport/PickupLocationInput";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useCallback, useEffect } from "react";
import { getAppointmentPhase } from "utilities";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenGroundTransportSidePanel } from "./GroundSidePanel";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateGroundTransportsCache } from "utilities/api/groundTransportQuery";

interface GroundTransportNewInfo {
  artist: server.dto.ArtistSmall | null
  phase: server.dto.AppointmentPhase
  travellers: server.dto.ContactSmallV2[]
  type: server.dto.GroundTransportType
  notes: string
  driver: server.dto.Driver

  duration: number

  date: GroundTransportDateTimeState

  startPickupLocationType: server.dto.PickupLocationType;
  startPickupLocationOther: string;

  endPickupLocationType: server.dto.PickupLocationType;
  endPickupLocationOther: string;

  startPlace: GooglePlaceInputValue;
  endPlace: GooglePlaceInputValue;

  directions: server.dto.GoogleDirections;
}

const NewGroundSidePanel = () => {
  const planningContext = React.useContext(PlanningContext);
  const openGroundSidePanel = useOpenGroundTransportSidePanel();

  const [provideDriverInformation, setProvideDriverInformation] = React.useState(false);
  const [value, setValue] = React.useState<GroundTransportNewInfo>({
    artist: planningContext.opts?.artist || null,
    phase: getAppointmentPhase("confirmed"),
    travellers: [],
    type: null,
    notes: "",
    driver: {
      name: null,
      mobile: null,
      email: null,
      vehicle: null
    },

    duration: 0,

    date: {
      departArrive: "departAt",
      dateTime: planningContext.opts?.date || moment.utc(),
      inTheNightOf: false,
    },

    startPickupLocationType: null,
    startPickupLocationOther: "",

    endPickupLocationType: null,
    endPickupLocationOther: "",

    startPlace: null,
    endPlace: null,

    directions: null,
  });

  useEffect(() => {
    const fetchRoute = async () => {
      if (value.startPlace && value.endPlace) {
        const response = await window.CoreApi.GoogleDirections.FetchDirections({
          originGooglePlaceId: value.startPlace.id,
          destinationGooglePlaceId: value.endPlace.id
        });

        if (response.data.hasErrors) {
          return;
        }

        const directions = response.data.data;
        setValue(v => ({ ...v, directions }));

        if (directions && directions.durationEstimateInMinutes > 0) {
          setValue(v => ({ ...v, duration: directions.durationEstimateInMinutes }));
        }
      }
    }
    fetchRoute();
  }, [value.startPlace?.id, value.endPlace?.id]);

  useEffect(() => {
    const fetchTravellers = async () => {
      if (value.artist?.artistId) {
        const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
          companyId: value.artist.artistId,
          defaultSelect: true,
          isActive: true,
          page: 1,
          perPage: 1000,
        });

        if (result.data.hasErrors) {
          return;
        }

        onChange({
          travellers: result.data.data.map(c => ({
            contactId: c.contact.contactId,
            culture: c.contact.culture,
            gender: c.contact.gender,
            isValidated: c.contact.isValidated,
            name: c.contact.name,
            position: c.contact.position,
            profileImageUrl: c.contact.profileImageUrl,
          }))
        });
      }
    }
    fetchTravellers();
  }, [value.artist?.artistId])

  const onDisplayDriverInfoChange = (checked: boolean) => {
    if (!checked) {
      setValue({
        ...value, driver: {
          name: null,
          mobile: null,
          email: null,
          vehicle: null,
        }
      });
    }
    setProvideDriverInformation(checked);
  }

  const onChange = (newData: Partial<GroundTransportNewInfo>) => {
    setValue({ ...value, ...newData });
  };

  const submit = () => {
    const instruction: server.dto.CreateGroundTransportInstruction = {
      artistId: value.artist?.artistId || null,
      dateTime: value.date.dateTime.toDate(),
      departOrArrive: value.date.departArrive,
      driver: value.driver,
      durationInMinutes: value.duration,
      endGooglePlaceAddress: value.endPlace?.address || null,
      endGooglePlaceGeo: value.endPlace?.geo || null,
      endGooglePlaceId: value.endPlace?.id || null,
      endGooglePlaceName: value.endPlace?.name || null,
      endPickupLocation: {
        value: value.endPickupLocationType.value,
        other: value.endPickupLocationOther,
      },
      linkToShowId: planningContext.opts?.fromShowId || null,
      groundTransportType: value.type ? value.type.value : "unknown",
      inTheNightOf: value.date.inTheNightOf,
      notes: value.notes,
      phase: value.phase.value,
      directions: {
        polyline: value.directions?.polyline,
        distanceInMeters: value.directions?.distanceInMeters,
        durationEstimateInMinutes: value.directions?.durationEstimateInMinutes,
        status: "",
      },
      startGooglePlaceAddress: value.startPlace?.address || null,
      startGooglePlaceGeo: value.startPlace?.geo || null,
      startGooglePlaceId: value.startPlace?.id || null,
      startGooglePlaceName: value.startPlace?.name || null,
      startPickupLocation: {
        value: value.startPickupLocationType.value,
        other: value.startPickupLocationOther,
      },
      travellers: value.travellers.map(t => t.contactId),
    };
    return window.CoreApi.GroundTransport.Create(instruction);
  }

  const queryClient = useQueryClient();

  return <SidePanelNewSectionTemplate
    onSave={id => {
      invalidateGroundTransportsCache(queryClient);
      openGroundSidePanel(id);
    }}
    title={RESX.GroundTransport.NewGroundTransport}
    id="new-ground-transport-side-panel"
    trigger={null}
    submit={submit}
    fields={[{
      key: "artist",
      label: site.currentEnvironment.functionGroups.artist.singular,
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          value={value.artist}
          onChange={artist => onChange({ artist })}
          validatorKey="artist"
          securityScope="limitByUserAccess"
          required
          canCreateNew={false}
        />
      </div>
    }, {
      key: "travellers",
      label: RESX.Flight.Travellers,
      renderEditNew: () => <div className="main-section-template-fields-padding">
        <ContactMultiModalPicker
          value={value.travellers}
          onChange={travellers => onChange({ travellers })}
          canCreateNew={false}
          required={false}
          validatorKey="contacts"
        />
      </div>
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      renderEditNew: () => <AppointmentPhaseDropdown
        value={value.phase}
        onChange={phase => onChange({ phase })}
      />
    }, {
      key: "transport",
      label: RESX.GroundTransport.Transport,
      renderEditNew: () => <GroundTransportTypeDropdown
        value={value.type}
        onChange={type => onChange({ type })}
      />
    }, {
      key: "notes",
      label: RESX.Note.Notes,
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.groundTransport.maxNotesLength}
        value={value.notes}
        onChange={notes => onChange({ notes })}
      />
    }, {
      key: "displayDriverInfo",
      label: RESX.GroundTransport.Driver,
      renderEditNew: () => <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
        <Checkbox
          checked={provideDriverInformation}
          onChange={() => onDisplayDriverInfoChange(!provideDriverInformation)} />
      </div>
    }, {
      key: "driverSectionHeader",
      label: "",
      hideLabel: true,
      hidden: !provideDriverInformation,
      renderEditNew: () => <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
        <h3>{RESX.GroundTransport.Driver}</h3>
      </div>
    }, {
      key: "driverName",
      label: RESX.GroundTransport.DriverName,
      hidden: !provideDriverInformation,
      renderEditNew: () => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.groundTransport.maxDriverNameLength}
        value={value.driver.name || ""}
        onChange={name => onChange({
          driver: {
            ...value.driver,
            name
          }
        })}
      />
    }, {
      key: "driverMobile",
      label: RESX.GroundTransport.DriverMobile,
      hidden: !provideDriverInformation,
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.groundTransport.maxDriverMobileLength}
        value={value.driver.mobile || ""}
        onChange={mobile => onChange({
          driver: {
            ...value.driver,
            mobile
          }
        })}
      />
    }, {
      key: "driverEmail",
      label: RESX.GroundTransport.DriverEmail,
      hidden: !provideDriverInformation,
      renderEditNew: () => <EmailInput
        maxLength={serverReference.constants.groundTransport.maxDriverEmailLength}
        value={value.driver.email || ""}
        onChange={email => onChange({
          driver: {
            ...value.driver,
            email
          }
        })}
        emailCanBeBlank
        validatorKey="driverEmail"
      />
    }, {
      key: "driverVehicleModel",
      label: RESX.GroundTransport.Vehicle,
      hidden: !provideDriverInformation,
      renderEditNew: () => <OptionalTextInput
        maxLength={serverReference.constants.groundTransport.maxDriverVehicleLength}
        value={value.driver.vehicle || ""}
        onChange={vehicle => onChange({
          driver: {
            ...value.driver,
            vehicle
          }
        })}
      />
    }, {
      key: "fromTo",
      label: "",
      hideLabel: true,
      renderEditNew: () => <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
        <h3>{RESX.Flight.Departure} / {RESX.Flight.Arrival}</h3>
      </div>
    }, {
      key: "dateTime",
      label: RESX.DateTime.lblDate,
      renderEditNew: () => <GroundTransportDateTime
        value={value.date}
        onChange={date => onChange({ date })}
      />
    }, {
      key: "duration",
      label: RESX.DateTime.Duration,
      renderEditNew: () => <GroundTransportDurationInput
        value={value.duration}
        googleEstimatedDurationMinutes={value.directions?.durationEstimateInMinutes}
        onChange={duration => onChange({ duration })}
      />
    }, {
      key: "destination",
      label: RESX.GroundTransport.Destination,
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <PickupLocationInput
          value={{
            pickupLocationType: value.startPickupLocationType,
            pickupLocationOther: value.startPickupLocationOther
          }}
          validatorKey="typeFrom"
          onChange={(v) => onChange({
            startPickupLocationType: v.pickupLocationType,
            startPickupLocationOther: v.pickupLocationOther
          })}
        />
        <PickupLocationInput
          value={{
            pickupLocationType: value.endPickupLocationType,
            pickupLocationOther: value.endPickupLocationOther
          }}
          validatorKey="typeTo"
          onChange={(v) => onChange({
            endPickupLocationType: v.pickupLocationType,
            endPickupLocationOther: v.pickupLocationOther
          })}
        />
      </div>
    }, {
      key: "address",
      label: RESX.Address.resxAddress,
      renderEditNew: () => <div className="main-section-template-two-column-grid">
        <GooglePlaceInputWithValue
          value={value.startPlace}
          onPlaceSelected={(v: GooglePlaceInputValue) => {
            if (v === null && value.startPlace?.id && value.endPlace?.id) {
              onChange({ startPlace: v, directions: null, duration: 0 });
            } else {
              onChange({ startPlace: v, directions: null });
            }
          }}
        />
        <GooglePlaceInputWithValue
          value={value.endPlace}
          onPlaceSelected={(v: GooglePlaceInputValue) => {
            if (v === null && value.startPlace?.id && value.endPlace?.id) {
              onChange({ endPlace: v, directions: null, duration: 0 });
            } else {
              onChange({ endPlace: v, directions: null });
            }
          }}
        />
      </div>
    }, {
      key: "map",
      label: "",
      hidden: !value.startPlace?.geo && !value.endPlace?.geo,
      hideLabel: true,
      renderEditNew: () => <div style={{ paddingTop: 8 }}>
        <GoogleMapRoute
          mapId="DEMO_MAP_ID"
          height={400}
          route={value.directions}
          startPlace={value.startPlace}
          endPlace={value.endPlace}
        />
      </div>
    }]}
  />
}

export const useOpenNewGroundSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewGroundSidePanel = useCallback(() => {
    openSidePanel(
      <NewGroundSidePanel />,
      "new-ground-transport-side-panel",
      "large"
    );
  }, [openSidePanel]);

  return openNewGroundSidePanel;
}